import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Camera, Music, Brain, CircleDot, Moon, Leaf, Heart, Wind, Star, Check, ChevronDown, Quote, ShoppingBag, Zap, Shield, Smile, Battery } from 'lucide-react';
import { Logo } from './Logo';
interface LandingPageProps {
  onClose: () => void;
  onOpenApp: () => void;
}
const BENEFITS = [{
  icon: Battery,
  title: 'Więcej energii każdego dnia',
  desc: 'Skończ z chronicznym zmęczeniem. Codzienne rytuały podnoszą Twoją wibrację i naturalnie zwiększają poziom energii.',
  feature: 'Skaner Aury AI + Codzienne Rytuały',
  color: 'from-amber-400 to-orange-500',
  bg: 'bg-amber-50',
  iconColor: 'text-amber-600'
}, {
  icon: Shield,
  title: 'Spokój w stresujących chwilach',
  desc: 'Naucz swój układ nerwowy reagować z gracją. Techniki oddechowe i terapia dźwiękiem aktywują nerw błędny.',
  feature: 'Oddech Pudełkowy + Częstotliwości Solfeggio',
  color: 'from-emerald-400 to-teal-500',
  bg: 'bg-emerald-50',
  iconColor: 'text-emerald-600'
}, {
  icon: Moon,
  title: 'Głęboki, regenerujący sen',
  desc: 'Zasypiaj szybciej i budź się wypoczęta. Fale Delta przygotowują Twój mózg do regeneracji.',
  feature: 'Program Głęboki Sen z falami Delta',
  color: 'from-indigo-400 to-purple-500',
  bg: 'bg-indigo-50',
  iconColor: 'text-indigo-600'
}, {
  icon: Heart,
  title: 'Większa pewność siebie',
  desc: 'Uwolnij blokujące przekonania i zastąp je nowymi kodami sukcesu. Praca z podświadomością na poziomie Theta.',
  feature: 'Detoks Przekonań + Afirmacje',
  color: 'from-rose-400 to-pink-500',
  bg: 'bg-rose-50',
  iconColor: 'text-rose-600'
}, {
  icon: Smile,
  title: 'Lepsze relacje z bliskimi',
  desc: 'Otwórz serce, zwiększ empatię i ulecz emocjonalne rany dzięki harmonizacji czakr.',
  feature: 'Oczyszczanie Czakr 7 ośrodków',
  color: 'from-fuchsia-400 to-purple-500',
  bg: 'bg-fuchsia-50',
  iconColor: 'text-fuchsia-600'
}, {
  icon: Sparkles,
  title: 'Pełna świadomość siebie',
  desc: 'Codzienne odczyty pomogą Ci lepiej rozumieć swoje emocje, ciało i potrzeby.',
  feature: 'AI Skaner + Śledzenie Somatyczne',
  color: 'from-cyan-400 to-blue-500',
  bg: 'bg-cyan-50',
  iconColor: 'text-cyan-600'
}];
const HOW_IT_WORKS = [{
  step: '01',
  title: 'Zeskanuj swoją aurę',
  desc: 'AI analizuje Twoje pole energetyczne i mikroekspresje twarzy, by stworzyć spersonalizowany odczyt.',
  icon: Camera
}, {
  step: '02',
  title: 'Otrzymaj rekomendację',
  desc: 'Dostajesz rytuał idealnie dopasowany do Twojego stanu — dźwięk, oddech i intencja.',
  icon: Sparkles
}, {
  step: '03',
  title: 'Praktykuj 5-10 minut',
  desc: 'Krótkie sesje, które łatwo zmieścisz w napiętym dniu. Mierzalne efekty od pierwszego razu.',
  icon: Wind
}, {
  step: '04',
  title: 'Obserwuj transformację',
  desc: 'Twoja Aura rośnie z każdą praktyką. Śledzisz progres i budujesz trwałe nawyki dobrostanu.',
  icon: Zap
}];
const TESTIMONIALS = [{
  name: 'Magdalena K.',
  role: 'CEO, mama dwójki dzieci',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop',
  text: 'Po 3 tygodniach z AuraSync śpię lepiej niż od lat. Codzienny rytuał porannego oddechu zmienił moje życie. Nareszcie czuję się sobą.',
  rating: 5
}, {
  name: 'Karolina B.',
  role: 'Coach biznesowy',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop',
  text: 'Detoks Przekonań to magia. Uwolniłam blokady, o których nawet nie wiedziałam. Mój biznes urósł 2x w 4 miesiące.',
  rating: 5
}, {
  name: 'Aleksandra M.',
  role: 'Lekarka, miłośniczka jogi',
  avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop',
  text: 'Częstotliwości Solfeggio przed snem to mój game-changer. Wreszcie nauczyłam się wyłączać po dyżurze.',
  rating: 5
}];
const PRODUCTS = [{
  name: 'Złoty Kamerton 528 Hz',
  desc: 'Częstotliwość Miłości i Naprawy DNA',
  img: 'https://images.unsplash.com/photo-1599305090598-fe179d501227?w=400&h=400&fit=crop',
  price: '249 zł',
  bonus: '+2500'
}, {
  name: 'Zestaw Aromaterapii',
  desc: 'Olejki do wieczornego rytuału',
  img: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=400&h=400&fit=crop',
  price: '149 zł',
  bonus: '+500'
}, {
  name: 'Mata Pranamat',
  desc: 'Głęboki relaks i akupresura',
  img: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=400&fit=crop',
  price: '389 zł',
  bonus: '+1200'
}];
const FAQ = [{
  q: 'Czy AuraSync to aplikacja medyczna?',
  a: 'Nie, AuraSync to narzędzie wspierające Twój dobrostan emocjonalny i mentalny. Nie zastępuje konsultacji z lekarzem czy terapeutą, ale jest doskonałym uzupełnieniem zdrowego stylu życia i pracy nad sobą.'
}, {
  q: 'Ile czasu dziennie potrzebuję na praktykę?',
  a: 'Wystarczy 5-10 minut dziennie. Większość naszych rytuałów to krótkie sesje, które łatwo zmieścisz nawet w najbardziej napiętym grafiku — między spotkaniami, w przerwie obiadowej albo przed snem.'
}, {
  q: 'Czy potrzebuję słuchawek do korzystania z aplikacji?',
  a: 'Słuchawki nie są wymagane, ale zdecydowanie polecane. Dzięki nim w pełni doświadczysz efektu częstotliwości Solfeggio i dźwięków binauralnych, które działają najlepiej w dźwięku przestrzennym.'
}, {
  q: 'Czy aplikacja działa w trybie offline?',
  a: 'Tak, większość funkcji (oddychanie, afirmacje, dźwięki) działa bez internetu. Skaner Aury AI wymaga połączenia, ponieważ analizuje obraz na serwerze.'
}, {
  q: 'Czy moje dane są bezpieczne?',
  a: 'Absolutnie. Twoje zdjęcia są analizowane przez AI i nie są przechowywane. Cała komunikacja jest szyfrowana, a my nie sprzedajemy danych osobowych — to obietnica.'
}, {
  q: 'Co dostanę za darmo, a za co muszę zapłacić?',
  a: 'Afirmacje, Oczyszczanie Czakr, Głęboki Sen, podstawowe częstotliwości i wiele innych funkcji są całkowicie darmowe. Premium funkcje (Skaner AI, Detoks Przekonań Theta) wymagają punktów energetycznych, które zdobywasz praktykując lub kupując suplementy w naszym Butiku.'
}];
const STATS = [{
  value: '50k+',
  label: 'Świadomych kobiet'
}, {
  value: '4.9',
  label: 'Średnia ocena'
}, {
  value: '12M+',
  label: 'Ukończonych rytuałów'
}, {
  value: '87%',
  label: 'Czuje różnicę w 7 dni'
}];
export function LandingPage({
  onClose,
  onOpenApp
}: LandingPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  return <motion.div className="fixed inset-0 z-50 bg-zen-bg overflow-y-auto overflow-x-hidden" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-608">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-zen-bg/80 backdrop-blur-xl border-b border-slate-100" data-id="element-609">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3" data-id="element-610">
          <Logo onClick={onClose} data-id="element-611" />
          <button onClick={onOpenApp} className="bg-zen-text text-white text-xs sm:text-sm font-bold px-4 sm:px-5 py-2 sm:py-2.5 rounded-full hover:bg-slate-800 transition-all shadow-sm" data-id="element-612">
            Otwórz aplikację
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative px-4 sm:px-6 pt-12 pb-20 sm:pt-20 sm:pb-32 overflow-hidden" data-id="element-613">
        {/* Background decorations */}
        <div className="absolute top-20 -right-20 w-80 h-80 bg-zen-sage/10 rounded-full blur-3xl" data-id="element-614" />
        <div className="absolute top-40 -left-20 w-96 h-96 bg-zen-accent/10 rounded-full blur-3xl" data-id="element-615" />

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-616">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-617">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-center md:text-left" data-id="element-618">
              <div className="inline-flex items-center space-x-2 bg-zen-accent/10 border border-zen-accent/20 px-3 py-1.5 rounded-full mb-6" data-id="element-619">
                <Sparkles className="w-3 h-3 text-zen-accent" fill="currentColor" data-id="element-620" />
                <span className="text-[10px] sm:text-xs font-bold text-zen-accent uppercase tracking-widest" data-id="element-621">
                  Dla świadomych kobiet
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-zen-text leading-tight mb-6 tracking-tight" data-id="element-622">
                Twój dobrostan
                <br data-id="element-623" />
                <span className="font-medium gold-gradient-text" data-id="element-624">
                  w 5 minut dziennie
                </span>
              </h1>

              <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-8 max-w-md mx-auto md:mx-0" data-id="element-625">
                Spersonalizowane rytuały oparte na neuronauce, terapii dźwiękiem
                i pracy z podświadomością. Dla kobiet, które chcą żyć w pełnej
                harmonii ze sobą.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start" data-id="element-626">
                <button onClick={onOpenApp} className="bg-emerald-600 text-white rounded-full py-4 px-8 text-sm font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20 flex items-center justify-center space-x-2" data-id="element-627">
                  <span data-id="element-628">Zacznij za darmo</span>
                  <ArrowRight className="w-4 h-4" data-id="element-629" />
                </button>
                <a href="#how" className="border border-slate-200 bg-white text-zen-text rounded-full py-4 px-8 text-sm font-bold hover:bg-slate-50 transition-all flex items-center justify-center" data-id="element-630">
                  Jak to działa?
                </a>
              </div>

              <div className="mt-8 flex items-center justify-center md:justify-start space-x-4 text-xs text-slate-400" data-id="element-631">
                <div className="flex -space-x-2" data-id="element-632">
                  {['https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop', 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop', 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop'].map((src, i) => <img key={i} src={src} alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" data-id="element-633" />)}
                </div>
                <div className="flex items-center space-x-1" data-id="element-634">
                  <div className="flex" data-id="element-635">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 text-yellow-400" fill="currentColor" data-id="element-636" />)}
                  </div>
                  <span className="font-semibold text-slate-600" data-id="element-637">4.9</span>
                  <span data-id="element-638">•</span>
                  <span data-id="element-639">50,000+ kobiet</span>
                </div>
              </div>
            </motion.div>

            {/* Hero visual - phone mockup */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative mx-auto" data-id="element-640">
              <div className="relative w-72 h-[560px] bg-gradient-to-br from-slate-900 to-slate-800 rounded-[3rem] p-3 shadow-2xl shadow-zen-accent/30" data-id="element-641">
                <div className="w-full h-full bg-gradient-to-b from-zen-bg to-emerald-50 rounded-[2.4rem] overflow-hidden relative" data-id="element-642">
                  {/* Aura visualization */}
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-643">
                    <motion.div className="w-48 h-48 rounded-full bg-gradient-to-br from-emerald-300 to-zen-sage" animate={{
                    scale: [1, 1.05, 1],
                    opacity: [0.4, 0.6, 0.4]
                  }} transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} style={{
                    filter: 'blur(40px)'
                  }} data-id="element-644" />
                    <motion.div className="absolute w-32 h-32 rounded-full bg-gradient-to-br from-yellow-200 to-amber-300" animate={{
                    scale: [1, 1.1, 1]
                  }} transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} style={{
                    filter: 'blur(20px)'
                  }} data-id="element-645" />
                    <div className="absolute w-24 h-24 rounded-full bg-white shadow-2xl border-2 border-zen-accent/30 flex items-center justify-center" data-id="element-646">
                      <Wind className="w-10 h-10 text-zen-accent" strokeWidth={1.5} data-id="element-647" />
                    </div>
                  </div>

                  {/* Top bar */}
                  <div className="absolute top-6 left-6 right-6 flex justify-between items-center" data-id="element-648">
                    <div className="text-[10px] font-bold text-zen-accent tracking-widest uppercase" data-id="element-649">
                      AuraSync
                    </div>
                    <div className="bg-yellow-50 px-2 py-1 rounded-full flex items-center space-x-1 border border-yellow-200" data-id="element-650">
                      <Sparkles className="w-2.5 h-2.5 text-yellow-500" fill="currentColor" data-id="element-651" />
                      <span className="text-[10px] font-bold text-yellow-600" data-id="element-652">
                        390
                      </span>
                    </div>
                  </div>

                  {/* Bottom status */}
                  <div className="absolute bottom-8 left-6 right-6 text-center" data-id="element-653">
                    <p className="text-xs text-slate-400 uppercase tracking-widest mb-1" data-id="element-654">
                      Status
                    </p>
                    <p className="text-sm font-semibold text-emerald-600" data-id="element-655">
                      Twoja wibracja wzrasta
                    </p>
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <motion.div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl px-4 py-3 border border-slate-100" animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-656">
                <div className="flex items-center space-x-2" data-id="element-657">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-658">
                    <Check className="w-4 h-4 text-emerald-600" data-id="element-659" />
                  </div>
                  <div data-id="element-660">
                    <p className="text-xs font-bold text-zen-text" data-id="element-661">
                      Rytuał ukończony
                    </p>
                    <p className="text-[10px] text-slate-400" data-id="element-662">+10 energii</p>
                  </div>
                </div>
              </motion.div>

              <motion.div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl px-4 py-3 border border-slate-100" animate={{
              y: [0, 8, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-663">
                <div className="flex items-center space-x-2" data-id="element-664">
                  <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center" data-id="element-665">
                    <Heart className="w-4 h-4 text-rose-500" fill="currentColor" data-id="element-666" />
                  </div>
                  <div data-id="element-667">
                    <p className="text-xs font-bold text-zen-text" data-id="element-668">528 Hz</p>
                    <p className="text-[10px] text-slate-400" data-id="element-669">
                      Częstotliwość Miłości
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="px-4 sm:px-6 py-12 bg-white border-y border-slate-100" data-id="element-670">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-671">
          {STATS.map((stat, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="text-center" data-id="element-672">
              <div className="text-3xl sm:text-4xl font-light gold-gradient-text mb-1" data-id="element-673">
                {stat.value}
              </div>
              <p className="text-xs text-slate-500" data-id="element-674">{stat.label}</p>
            </motion.div>)}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="px-4 sm:px-6 py-20 sm:py-32" data-id="element-675">
        <div className="max-w-6xl mx-auto" data-id="element-676">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16 max-w-2xl mx-auto" data-id="element-677">
            <div className="inline-flex items-center space-x-2 bg-zen-sage/10 border border-zen-sage/20 px-3 py-1.5 rounded-full mb-4" data-id="element-678">
              <Heart className="w-3 h-3 text-zen-sage" fill="currentColor" data-id="element-679" />
              <span className="text-[10px] sm:text-xs font-bold text-zen-sage uppercase tracking-widest" data-id="element-680">
                Korzyści
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-zen-text mb-4 leading-tight" data-id="element-681">
              Co zyskasz w 30 dni?
            </h2>
            <p className="text-base text-slate-500 leading-relaxed" data-id="element-682">
              Konkretne, mierzalne zmiany w Twoim ciele, umyśle i relacjach.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-683">
            {BENEFITS.map((benefit, i) => {
            const Icon = benefit.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.05
            }} className="clean-panel rounded-3xl p-6 relative overflow-hidden group" data-id="element-684">
                  <div className={`w-14 h-14 rounded-2xl ${benefit.bg} flex items-center justify-center mb-4 border border-slate-100`} data-id="element-685">
                    <Icon className={`w-7 h-7 ${benefit.iconColor}`} strokeWidth={1.5} data-id="element-686" />
                  </div>
                  <h3 className="text-lg font-semibold text-zen-text mb-2" data-id="element-687">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-4" data-id="element-688">
                    {benefit.desc}
                  </p>
                  <div className="pt-4 border-t border-slate-100" data-id="element-689">
                    <p className="text-[10px] font-bold text-zen-accent uppercase tracking-widest mb-1" data-id="element-690">
                      Wspierane przez
                    </p>
                    <p className="text-xs text-slate-600 font-medium" data-id="element-691">
                      {benefit.feature}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="px-4 sm:px-6 py-20 sm:py-32 bg-gradient-to-br from-zen-bg via-emerald-50/30 to-zen-bg" data-id="element-692">
        <div className="max-w-6xl mx-auto" data-id="element-693">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16 max-w-2xl mx-auto" data-id="element-694">
            <div className="inline-flex items-center space-x-2 bg-zen-accent/10 border border-zen-accent/20 px-3 py-1.5 rounded-full mb-4" data-id="element-695">
              <Sparkles className="w-3 h-3 text-zen-accent" fill="currentColor" data-id="element-696" />
              <span className="text-[10px] sm:text-xs font-bold text-zen-accent uppercase tracking-widest" data-id="element-697">
                Jak to działa
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-zen-text mb-4 leading-tight" data-id="element-698">
              4 proste kroki do harmonii
            </h2>
            <p className="text-base text-slate-500 leading-relaxed" data-id="element-699">
              Aplikacja prowadzi Cię za rękę przez cały proces.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-700">
            {HOW_IT_WORKS.map((step, i) => {
            const Icon = step.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="relative" data-id="element-701">
                  <div className="clean-panel rounded-3xl p-6 h-full relative" data-id="element-702">
                    <span className="absolute -top-4 -left-2 text-6xl font-light text-zen-accent/15 select-none" data-id="element-703">
                      {step.step}
                    </span>
                    <div className="w-12 h-12 rounded-2xl bg-zen-accent/10 flex items-center justify-center mb-4 relative z-10" data-id="element-704">
                      <Icon className="w-6 h-6 text-zen-accent" strokeWidth={1.5} data-id="element-705" />
                    </div>
                    <h3 className="text-base font-semibold text-zen-text mb-2 relative z-10" data-id="element-706">
                      {step.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed relative z-10" data-id="element-707">
                      {step.desc}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>

          <div className="text-center mt-12" data-id="element-708">
            <button onClick={onOpenApp} className="bg-zen-text text-white rounded-full py-4 px-8 text-sm font-bold hover:bg-slate-800 transition-all shadow-lg inline-flex items-center space-x-2" data-id="element-709">
              <span data-id="element-710">Wypróbuj teraz</span>
              <ArrowRight className="w-4 h-4" data-id="element-711" />
            </button>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="px-4 sm:px-6 py-20 sm:py-32" data-id="element-712">
        <div className="max-w-6xl mx-auto" data-id="element-713">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16 max-w-2xl mx-auto" data-id="element-714">
            <div className="inline-flex items-center space-x-2 bg-rose-50 border border-rose-100 px-3 py-1.5 rounded-full mb-4" data-id="element-715">
              <Heart className="w-3 h-3 text-rose-500" fill="currentColor" data-id="element-716" />
              <span className="text-[10px] sm:text-xs font-bold text-rose-600 uppercase tracking-widest" data-id="element-717">
                Społeczność
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-zen-text mb-4 leading-tight" data-id="element-718">
              Kobiety, które zaufały AuraSync
            </h2>
            <p className="text-base text-slate-500 leading-relaxed" data-id="element-719">
              Dołącz do tysięcy kobiet, które codziennie dbają o swój dobrostan.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-720">
            {TESTIMONIALS.map((testimonial, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="clean-panel rounded-3xl p-6 relative" data-id="element-721">
                <Quote className="absolute top-4 right-4 w-8 h-8 text-zen-accent/20" data-id="element-722" />
                <div className="flex items-center space-x-3 mb-4" data-id="element-723">
                  <img src={testimonial.avatar} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm" data-id="element-724" />
                  <div data-id="element-725">
                    <p className="text-sm font-semibold text-zen-text" data-id="element-726">
                      {testimonial.name}
                    </p>
                    <p className="text-[11px] text-slate-400" data-id="element-727">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-0.5 mb-3" data-id="element-728">
                  {[...Array(testimonial.rating)].map((_, idx) => <Star key={idx} className="w-4 h-4 text-yellow-400" fill="currentColor" data-id="element-729" />)}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed italic" data-id="element-730">
                  "{testimonial.text}"
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="px-4 sm:px-6 py-20 sm:py-32 bg-white border-y border-slate-100" data-id="element-731">
        <div className="max-w-6xl mx-auto" data-id="element-732">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16 max-w-2xl mx-auto" data-id="element-733">
            <div className="inline-flex items-center space-x-2 bg-yellow-50 border border-yellow-100 px-3 py-1.5 rounded-full mb-4" data-id="element-734">
              <ShoppingBag className="w-3 h-3 text-yellow-600" data-id="element-735" />
              <span className="text-[10px] sm:text-xs font-bold text-yellow-700 uppercase tracking-widest" data-id="element-736">
                Butik Dobrostanu
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-zen-text mb-4 leading-tight" data-id="element-737">
              Przenieś praktykę do realu
            </h2>
            <p className="text-base text-slate-500 leading-relaxed" data-id="element-738">
              Starannie wyselekcjonowane akcesoria wzmacniające efekt aplikacji.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6" data-id="element-739">
            {PRODUCTS.map((product, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="clean-panel rounded-3xl overflow-hidden group" data-id="element-740">
                <div className="relative aspect-square overflow-hidden bg-slate-50" data-id="element-741">
                  <img src={product.img} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-742" />
                  <div className="absolute top-3 right-3 bg-yellow-100 text-yellow-700 text-xs px-3 py-1 rounded-full font-bold flex items-center border border-yellow-200" data-id="element-743">
                    {product.bonus}{' '}
                    <Sparkles className="w-3 h-3 ml-1" fill="currentColor" data-id="element-744" />
                  </div>
                </div>
                <div className="p-5" data-id="element-745">
                  <h4 className="text-base font-semibold text-zen-text mb-1" data-id="element-746">
                    {product.name}
                  </h4>
                  <p className="text-xs text-slate-500 mb-4" data-id="element-747">{product.desc}</p>
                  <div className="flex items-center justify-between" data-id="element-748">
                    <span className="text-lg font-bold text-zen-text" data-id="element-749">
                      {product.price}
                    </span>
                    <button onClick={onOpenApp} className="bg-zen-text text-white text-xs font-bold px-4 py-2 rounded-full hover:bg-slate-800 transition-colors" data-id="element-750">
                      Kup teraz
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 sm:px-6 py-20 sm:py-32" data-id="element-751">
        <div className="max-w-3xl mx-auto" data-id="element-752">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-753">
            <div className="inline-flex items-center space-x-2 bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-full mb-4" data-id="element-754">
              <span className="text-[10px] sm:text-xs font-bold text-slate-600 uppercase tracking-widest" data-id="element-755">
                FAQ
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-zen-text mb-4 leading-tight" data-id="element-756">
              Najczęstsze pytania
            </h2>
            <p className="text-base text-slate-500 leading-relaxed" data-id="element-757">
              Nie znalazłaś odpowiedzi? Napisz do nas.
            </p>
          </motion.div>

          <div className="space-y-3" data-id="element-758">
            {FAQ.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="clean-panel rounded-2xl overflow-hidden" data-id="element-759">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors" data-id="element-760">
                  <span className="text-sm font-semibold text-zen-text pr-4" data-id="element-761">
                    {item.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-762" />
                </button>
                <AnimatePresence data-id="element-763">
                  {openFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-764">
                      <p className="px-5 pb-5 text-sm text-slate-500 leading-relaxed" data-id="element-765">
                        {item.a}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 sm:px-6 py-20 sm:py-32" data-id="element-766">
        <div className="max-w-4xl mx-auto" data-id="element-767">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative bg-gradient-to-br from-emerald-600 via-zen-sage to-emerald-700 rounded-[2rem] sm:rounded-[3rem] p-10 sm:p-16 text-center overflow-hidden shadow-2xl shadow-emerald-600/30" data-id="element-768">
            {/* Background decorations */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20" data-id="element-769" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-200/20 rounded-full blur-3xl -ml-20 -mb-20" data-id="element-770" />

            <div className="relative z-10" data-id="element-771">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6 border border-white/30" data-id="element-772">
                <Sparkles className="w-8 h-8 text-white" fill="currentColor" data-id="element-773" />
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-white mb-4 leading-tight" data-id="element-774">
                Twoja transformacja
                <br data-id="element-775" />
                <span className="font-medium" data-id="element-776">zaczyna się dziś</span>
              </h2>
              <p className="text-base sm:text-lg text-white/80 mb-8 max-w-lg mx-auto leading-relaxed" data-id="element-777">
                Dołącz do 50 000+ kobiet, które codziennie wybierają siebie.
                Pierwsze 7 dni za darmo.
              </p>
              <button onClick={onOpenApp} className="bg-white text-emerald-700 rounded-full py-4 px-10 text-sm font-bold hover:bg-slate-50 transition-all shadow-xl inline-flex items-center space-x-2 group" data-id="element-778">
                <span data-id="element-779">Otwórz aplikację</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-780" />
              </button>
              <p className="text-xs text-white/60 mt-6" data-id="element-781">
                ✦ Bez karty kredytowej ✦ Anuluj w każdej chwili ✦
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-4 sm:px-6 py-12 border-t border-slate-100 bg-zen-bg" data-id="element-782">
        <div className="max-w-6xl mx-auto" data-id="element-783">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0" data-id="element-784">
            <div className="flex items-center space-x-3" data-id="element-785">
              <Logo onClick={onClose} data-id="element-786" />
            </div>
            <div className="flex items-center space-x-6 text-xs text-slate-400" data-id="element-787">
              <a href="#" className="hover:text-zen-accent transition-colors" data-id="element-788">
                Polityka prywatności
              </a>
              <a href="#" className="hover:text-zen-accent transition-colors" data-id="element-789">
                Regulamin
              </a>
              <a href="#" className="hover:text-zen-accent transition-colors" data-id="element-790">
                Kontakt
              </a>
            </div>
          </div>
          <div className="text-center mt-8 text-xs text-slate-400" data-id="element-791">
            © 2026 AuraSync. Stworzone z miłością dla świadomych kobiet. 💚
          </div>
        </div>
      </footer>
    </motion.div>;
}